import Select from '@codegouvfr/react-dsfr/Select';
import {
  type Department,
  DepartmentLabels,
  DepartmentList
} from 'maestro-shared/referential/Department';
import { type Region, Regions } from 'maestro-shared/referential/Region';
import type { FindSampleOptions } from 'maestro-shared/schema/Sample/FindSampleOptions';
import { type FunctionComponent, useMemo } from 'react';
import { t } from 'src/i18n';
import { assert, type Equals } from 'tsafe';
import { useAuthentication } from '../../hooks/useAuthentication';

type Props = {
  id: string;
  filters: Partial<FindSampleOptions>;
  onSelect: (department: Department) => void;
};

export const DepartmentsSelect: FunctionComponent<Props> = ({
  id,
  filters,
  onSelect,
  ..._rest
}) => {
  assert<Equals<keyof typeof _rest, never>>();

  const { hasNationalView, user } = useAuthentication();

  const departmentOptions = useMemo(() => {
    const regions = hasNationalView
      ? (filters.regions ?? [])
      : user?.region
        ? [user.region]
        : [];
    const list =
      regions.length > 0
        ? Array.from(
            new Set(regions.flatMap((r) => Regions[r as Region].departments))
          )
        : DepartmentList;

    return list
      .filter((l) => !filters.departments?.includes(l))
      .sort((a, b) => a.localeCompare(b));
  }, [hasNationalView, user?.region, filters.regions, filters.departments]);

  const borderingDepartments = useMemo(() => {
    const regions = hasNationalView
      ? (filters.regions ?? [])
      : user?.region
        ? [user.region]
        : [];
    const list = Array.from(
      new Set(
        regions.flatMap((r) =>
          Regions[r as Region].borderingDepartments.sort((a, b) =>
            a.localeCompare(b)
          )
        )
      )
    );
    return list.filter((l) => !filters.departments?.includes(l));
  }, [hasNationalView, user?.region, filters.regions, filters.departments]);

  return (
    <Select
      label="Département"
      nativeSelectProps={{
        value: '',
        onChange: (e) => onSelect(e.target.value as Department)
      }}
    >
      <option value="">
        {filters.departments?.length
          ? t('department', {
              count: filters.departments?.length
            })
          : 'Tous'}
      </option>
      {borderingDepartments.length ? (
        <>
          <optgroup label={'Mes départements'}>
            {departmentOptions.map((department) => (
              <option key={`department-${department}`} value={department}>
                {`${department} - ${DepartmentLabels[department]}`}
              </option>
            ))}
          </optgroup>
          <optgroup label={'Départements limitrophes'}>
            {borderingDepartments.map((department) => (
              <option key={`department-${department}`} value={department}>
                {`${department} - ${DepartmentLabels[department]}`}
              </option>
            ))}
          </optgroup>
        </>
      ) : (
        departmentOptions.map((department) => (
          <option key={`department-${department}`} value={department}>
            {`${department} - ${DepartmentLabels[department]}`}
          </option>
        ))
      )}
    </Select>
  );
};
